import { Building2, Check, ChevronsUpDown } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { cn } from '@/lib/utils'

export function WorkspaceSelector() {
  const { currentWorkspace, workspaces, switchWorkspace, isLoading } = useWorkspace()

  if (isLoading || !currentWorkspace) {
    return (
      <div className="flex items-center gap-4 px-4 h-9">
        <div className="w-32 h-8 bg-muted animate-pulse rounded" />
      </div>
    )
  }

  // If user only has one workspace, show it as a static element
  if (workspaces.length === 1) {
    return (
      <div className="flex items-center gap-4 px-4">
        <div className="flex items-center gap-4">
          {currentWorkspace.logoUrl
            ? (
                <Avatar className="h-8 w-8">
                  <AvatarImage src={currentWorkspace.logoUrl} alt={currentWorkspace.name} />
                  <AvatarFallback>
                    <Building2 className="h-4 w-4" />
                  </AvatarFallback>
                </Avatar>
              )
            : (
                <div className="flex items-center justify-center h-8 w-8 rounded bg-muted">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                </div>
              )}
          <span className="text-sm font-medium">{currentWorkspace.name}</span>
        </div>
      </div>
    )
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          className="w-[300px] justify-between gap-2 h-9 overflow-hidden"
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {currentWorkspace.logoUrl
              ? (
                  <Avatar className="shrink-0 flex items-center">
                    <Building2 className="h-3 w-3" />
                  </Avatar>
                )
              : (
                  <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                )}
            <span className="truncate">{currentWorkspace.name}</span>
          </div>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0">
        <Command>
          <CommandInput placeholder="Search workspace..." />
          <CommandList>
            <CommandEmpty>No workspace found.</CommandEmpty>
            <CommandGroup>
              {workspaces.map(workspace => (
                <CommandItem
                  key={workspace.id}
                  value={workspace.name}
                  onSelect={() => {
                    switchWorkspace(workspace.id)
                  }}
                >
                  <div className="flex items-center gap-4 w-full">
                    {workspace.logoUrl
                      ? (
                          <Avatar className="h-5 w-5">
                            <AvatarImage src={workspace.logoUrl} alt={workspace.name} />
                            <AvatarFallback>
                              <Building2 className="h-3 w-3" />
                            </AvatarFallback>
                          </Avatar>
                        )
                      : (
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                        )}
                    <span className="truncate">{workspace.name}</span>
                    <Check
                      className={cn(
                        'ml-auto h-4 w-4',
                        currentWorkspace.id === workspace.id ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
